import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  CalendarIcon,
  PhotoIcon,
  StarIcon,
  BuildingStorefrontIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChartBarIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ReceiptPercentIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import LocuLogo from '../components/LocuLogo';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LocuMerchantDashboard = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [restaurantMenuOpen, setRestaurantMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const restaurantMenuRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== 'merchant') {
      navigate('/login');
      return;
    }
    fetchRestaurants();
  }, [user, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (restaurantMenuRef.current && !restaurantMenuRef.current.contains(event.target)) {
        setRestaurantMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurants/my/restaurants`);
      setRestaurants(response.data);
      if (response.data.length > 0) {
        setSelectedRestaurant(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigationItems = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'redemptions', name: 'Redemptions', icon: ReceiptPercentIcon },
    { id: 'commission', name: 'Commission', icon: BanknotesIcon },
    { id: 'bookings', name: 'Bookings', icon: CalendarIcon },
    { id: 'photos', name: 'Photos', icon: PhotoIcon },
    { id: 'info', name: 'Info', icon: BuildingStorefrontIcon },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <Overview restaurant={selectedRestaurant} />;
      case 'redemptions':
        return <Redemptions restaurant={selectedRestaurant} />;
      case 'commission':
        return <Commission restaurant={selectedRestaurant} />;
      case 'bookings':
        return <Bookings restaurant={selectedRestaurant} />;
      case 'photos':
        return <Photos restaurant={selectedRestaurant} />;
      case 'info':
        return <Info restaurant={selectedRestaurant} />;
      case 'profile':
        return <Profile />;
      default:
        return <Overview restaurant={selectedRestaurant} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } bg-gray-900 text-white transition-all duration-300 overflow-hidden flex flex-col fixed lg:relative h-screen z-40`}
      >
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LocuLogo className="h-9 w-9" />
            <div>
              <div className="font-bold text-sm">LOCU</div>
              <div className="text-xs text-gray-400">MERCHANT PARTNER</div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-2 space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1 text-left text-sm font-medium">{item.name}</span>
                  {item.badge && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="hidden lg:flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center font-bold text-lg text-white">
                L
              </div>
              <div>
                <div className="font-bold text-sm text-gray-900">LOCU</div>
                <div className="text-xs text-gray-500">RESTAURANT PARTNER</div>
              </div>
            </div>
          </div>

          {/* Restaurant Selector */}
          {restaurants.length > 1 && (
            <div className="flex-1 flex justify-center">
              <div className="relative" ref={restaurantMenuRef}>
                <button
                  onClick={() => setRestaurantMenuOpen(!restaurantMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <BuildingStorefrontIcon className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {selectedRestaurant?.name || 'Select Restaurant'}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </button>
                {restaurantMenuOpen && (
                  <div className="absolute top-full mt-2 left-0 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase">
                        Select Restaurant ({restaurants.length})
                      </p>
                    </div>
                    {restaurants.map((restaurant) => (
                      <button
                        key={restaurant._id}
                        onClick={() => {
                          setSelectedRestaurant(restaurant);
                          setRestaurantMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          selectedRestaurant?._id === restaurant._id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {restaurant.name}
                            </p>
                            {restaurant.address && (
                              <p className="text-xs text-gray-500 truncate mt-0.5">
                                {restaurant.address}
                              </p>
                            )}
                            {restaurant.cuisine && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                {restaurant.cuisine}
                              </span>
                            )}
                          </div>
                          {selectedRestaurant?._id === restaurant._id && (
                            <CheckCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <button className="text-gray-600 hover:text-gray-900 text-sm font-medium">
              Request support
            </button>
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
              >
                <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user?.email?.charAt(0)?.toUpperCase() || 'E'}
                </div>
                <span className="hidden md:block text-sm font-medium">{user?.email || 'Email'}</span>
                <ChevronDownIcon className="h-4 w-4" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-lg border border-gray-700 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-700">
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="text-sm font-medium text-white">{user?.email}</p>
                  </div>
                  <div className="pt-2">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        setActiveSection('profile');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800"
                    >
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                        navigate('/');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-800"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>


        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

// Overview Component
const Overview = ({ restaurant }) => {
  const [stats, setStats] = useState({ totalVolume: 0, transactionCount: 0, totalCommission: 0 });
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (restaurant?._id) {
      fetchStats();
    }
  }, [restaurant?._id]);

  const fetchStats = async () => {
    try {
      const now = new Date();
      const [commRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/transactions/restaurant/${restaurant._id}/commission/${now.getFullYear()}/${now.getMonth() + 1}`),
        axios.get(`${API_URL}/transactions/restaurant/${restaurant._id}/pending`)
      ]);
      if (commRes.data.commission) {
        setStats(commRes.data.commission);
      }
      setPendingCount(Array.isArray(pendingRes.data) ? pendingRes.data.length : 0);
    } catch (error) {
      console.error('Error fetching overview stats:', error);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <BanknotesIcon className="h-6 w-6 text-green-600" />
            <h3 className="text-sm font-medium text-gray-600">Revenue</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">₹{(stats.totalVolume || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-2">This month (confirmed)</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <ReceiptPercentIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-sm font-medium text-gray-600">Transactions</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.transactionCount || 0}</p>
          <p className="text-sm text-gray-500 mt-2">Confirmed this month</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <CalendarIcon className="h-6 w-6 text-orange-600" />
            <h3 className="text-sm font-medium text-gray-600">Pending</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{pendingCount}</p>
          <p className="text-sm text-gray-500 mt-2">Awaiting confirmation</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <PhotoIcon className="h-6 w-6 text-purple-600" />
            <h3 className="text-sm font-medium text-gray-600">Photos</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{restaurant?.photos?.length || 0}</p>
          <p className="text-sm text-gray-500 mt-2">Uploaded</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Restaurant Details</h2>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Name</span>
            <span className="font-medium text-gray-900">{restaurant?.name || 'N/A'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Cuisine</span>
            <span className="font-medium text-gray-900">{restaurant?.cuisine || 'N/A'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Address</span>
            <span className="font-medium text-gray-900 text-right max-w-md">{restaurant?.address || 'N/A'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Phone</span>
            <span className="font-medium text-gray-900">{restaurant?.phone || 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bookings Component
const Bookings = ({ restaurant }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (restaurant?._id) {
      fetchEvents();
    }
  }, [restaurant?._id]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/dining-events/restaurant/${restaurant._id}`
      );
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (error) {
      console.error('Error fetching dining events:', error);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();

  const upcomingEvents = events.filter(e =>
    new Date(e.eventDate) >= now && ['open', 'full'].includes(e.status)
  );
  const pastEvents = events.filter(e =>
    new Date(e.eventDate) < now || e.status === 'completed'
  );
  const cancelledEvents = events.filter(e => e.status === 'cancelled');

  const getDisplayEvents = () => {
    let filtered;
    switch (activeTab) {
      case 'upcoming': filtered = upcomingEvents; break;
      case 'past': filtered = pastEvents; break;
      case 'cancelled': filtered = cancelledEvents; break;
      default: filtered = upcomingEvents;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.organizer?.name?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      full: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const [expandedEventId, setExpandedEventId] = useState(null);

  const displayEvents = getDisplayEvents();
  const tabs = [
    { key: 'upcoming', label: 'Upcoming', count: upcomingEvents.length },
    { key: 'past', label: 'Past', count: pastEvents.length },
    { key: 'cancelled', label: 'Cancelled', count: cancelledEvents.length },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dining Events</h1>

      <div className="flex gap-2 mb-4">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by organizer or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 border-0 focus:ring-0 text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organizer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayEvents.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <CalendarIcon className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          No dining events yet
                        </p>
                        <p className="text-sm text-gray-500">
                          Dining events at your restaurant will appear here
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayEvents.map((event) => (
                    <React.Fragment key={event.id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {event.organizer?.photo ? (
                              <img src={event.organizer.photo} alt="" className="h-8 w-8 rounded-full object-cover mr-3" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold mr-3">
                                {event.organizer?.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {event.organizer?.name || 'Unknown'}
                              </span>
                              <p className="text-xs text-gray-400">Tap to {expandedEventId === event.id ? 'collapse' : 'see guests'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {event.participantsCount}/{event.totalSeats}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(event.eventDate)} at {formatTime12h(event.eventTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadge(event.status)}`}>
                            {event.status}
                          </span>
                        </td>
                      </tr>
                      {expandedEventId === event.id && (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <p className="text-sm font-semibold text-gray-700">
                                Guest List ({event.participants?.length || 0} confirmed)
                              </p>
                              {event.description && (
                                <p className="text-sm text-gray-500 italic">{event.description}</p>
                              )}
                              <div className="divide-y divide-gray-200">
                                {event.participants?.map((p, idx) => (
                                  <div key={idx} className="flex items-center justify-between py-2">
                                    <div className="flex items-center gap-3">
                                      {p.user?.photo ? (
                                        <img src={p.user.photo} alt="" className="h-7 w-7 rounded-full object-cover" />
                                      ) : (
                                        <div className="h-7 w-7 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-xs font-semibold">
                                          {p.user?.name?.charAt(0) || '?'}
                                        </div>
                                      )}
                                      <div>
                                        <span className="text-sm font-medium text-gray-900">
                                          {p.user?.name || 'Unknown'}
                                          {p.user?._id === event.organizer?._id && (
                                            <span className="ml-1 text-xs text-primary-600">(Organizer)</span>
                                          )}
                                        </span>
                                        {p.user?.phone && (
                                          <p className="text-xs text-gray-500">{p.user.phone}</p>
                                        )}
                                      </div>
                                    </div>
                                    {p.checkedIn && (
                                      <span className="text-xs text-green-600 font-medium">Checked in</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Redemptions Component
const Redemptions = ({ restaurant }) => {
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [allRedemptions, setAllRedemptions] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedRedemption, setSelectedRedemption] = useState(null);

  useEffect(() => {
    if (restaurant) {
      fetchRedemptions();
    }
  }, [restaurant]);

  const fetchRedemptions = async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/transactions/restaurant/${restaurant._id}/pending`),
        axios.get(`${API_URL}/transactions/restaurant/${restaurant._id}`)
      ]);
      setPendingRedemptions(pendingRes.data);
      setAllRedemptions(allRes.data);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (transactionId) => {
    try {
      await axios.post(`${API_URL}/transactions/${transactionId}/confirm`);
      alert('Redemption confirmed successfully!');
      fetchRedemptions();
      setSelectedRedemption(null);
    } catch (error) {
      console.error('Error confirming redemption:', error);
      alert('Failed to confirm redemption');
    }
  };

  const handleDispute = async (transactionId) => {
    const notes = prompt('Please provide reason for dispute:');
    if (!notes) return;

    try {
      await axios.post(`${API_URL}/transactions/${transactionId}/dispute`, {
        merchantNotes: notes
      });
      alert('Redemption disputed. Member points refunded.');
      fetchRedemptions();
      setSelectedRedemption(null);
    } catch (error) {
      console.error('Error disputing redemption:', error);
      alert('Failed to dispute redemption');
    }
  };

  const RedemptionModal = ({ redemption, onClose }) => {
    if (!redemption) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Redemption Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Member Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Member Information</h3>
              <div className="space-y-2">
                <p><span className="text-gray-600">Name:</span> <span className="font-medium">{redemption.member?.name}</span></p>
                <p><span className="text-gray-600">Phone:</span> <span className="font-medium">{redemption.member?.phone}</span></p>
                <p><span className="text-gray-600">Email:</span> <span className="font-medium">{redemption.member?.email}</span></p>
              </div>
            </div>

            {/* Bill Details */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Bill Details</h3>
              <div className="space-y-2">
                <p className="text-2xl font-bold text-blue-600">₹{redemption.originalBillAmount?.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Original Bill Amount</p>
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <p><span className="text-gray-600">Discount:</span> <span className="font-medium text-green-600">-₹{redemption.discountAmount} ({redemption.discountPercentage}%)</span></p>
                  <p><span className="text-gray-600">Amount Paid:</span> <span className="font-medium">₹{redemption.finalAmount?.toLocaleString()}</span></p>
                  <p><span className="text-gray-600">Points Used:</span> <span className="font-medium">{redemption.pointsDeducted}</span></p>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className={`rounded-lg p-4 ${redemption.paymentMethod === 'cash' ? 'bg-yellow-50' : 'bg-green-50'}`}>
              <h3 className="font-semibold text-gray-900 mb-2">Payment Method</h3>
              <p className={`text-lg font-bold ${redemption.paymentMethod === 'cash' ? 'text-yellow-700' : 'text-green-700'}`}>
                {redemption.paymentMethod === 'cash' ? '💵 Cash Payment' : '📱 UPI Payment'}
              </p>
              {redemption.paymentMethod === 'cash' && (
                <p className="text-sm text-yellow-700 mt-1">Please confirm you received ₹{redemption.finalAmount} in cash</p>
              )}
              {redemption.paymentVerified && (
                <p className="text-sm text-green-700 mt-1">✓ Payment verified automatically</p>
              )}
            </div>

            {/* Bill Image */}
            {redemption.billImage && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Restaurant Bill</h3>
                <img
                  src={redemption.billImage}
                  alt="Restaurant bill"
                  className="w-full max-h-64 object-contain rounded-lg border border-gray-300"
                />
              </div>
            )}

            {/* Payment Screenshot (for UPI) */}
            {redemption.paymentMethod === 'upi' && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Payment Screenshot</h3>
                {redemption.paymentScreenshot ? (
                  <img
                    src={redemption.paymentScreenshot}
                    alt="Payment proof"
                    className="w-full max-h-64 object-contain rounded-lg border border-gray-300"
                  />
                ) : (
                  <p className="text-gray-500">No screenshot uploaded</p>
                )}
                {redemption.upiTransactionId && (
                  <p className="mt-2 text-sm"><span className="text-gray-600">UPI Transaction ID:</span> <span className="font-mono">{redemption.upiTransactionId}</span></p>
                )}
                {redemption.paymentOcrAmount && (
                  <p className="mt-1 text-sm"><span className="text-gray-600">OCR Detected Amount:</span> <span className="font-medium">₹{redemption.paymentOcrAmount}</span></p>
                )}
              </div>
            )}

            {/* Transaction Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Transaction Info</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-600">Date:</span> <span className="font-medium">{new Date(redemption.createdAt).toLocaleString()}</span></p>
                <p><span className="text-gray-600">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    redemption.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    redemption.status === 'verified' ? 'bg-blue-100 text-blue-800' :
                    redemption.status === 'disputed' ? 'bg-red-100 text-red-800' :
                    redemption.status === 'pending_merchant' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {redemption.status === 'pending_merchant' ? 'Awaiting Cash Confirmation' :
                     redemption.status === 'pending_verification' ? 'Payment Verification Pending' :
                     redemption.status === 'verified' ? 'Payment Verified' :
                     redemption.status}
                  </span>
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {['pending_confirmation', 'pending_merchant', 'pending_verification'].includes(redemption.status) && (
              <div className="flex gap-3">
                <button
                  onClick={() => handleConfirm(redemption._id)}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
                >
                  Confirm Payment
                </button>
                <button
                  onClick={() => handleDispute(redemption._id)}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Dispute
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Redemptions</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 px-1 border-b-2 font-medium transition ${
              activeTab === 'pending'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Pending ({pendingRedemptions.length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 px-1 border-b-2 font-medium transition ${
              activeTab === 'all'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All Transactions ({allRedemptions.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Final Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(activeTab === 'pending' ? pendingRedemptions : allRedemptions).length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No redemptions found
                    </td>
                  </tr>
                ) : (
                  (activeTab === 'pending' ? pendingRedemptions : allRedemptions).map((redemption) => (
                    <tr key={redemption._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{redemption.member?.name}</div>
                        <div className="text-sm text-gray-500">{redemption.member?.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">₹{redemption.originalBillAmount?.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-green-600">-₹{redemption.discountAmount} ({redemption.discountPercentage}%)</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{redemption.finalAmount?.toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full inline-block w-fit ${
                            redemption.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            redemption.status === 'verified' ? 'bg-blue-100 text-blue-800' :
                            redemption.status === 'disputed' ? 'bg-red-100 text-red-800' :
                            redemption.status === 'pending_merchant' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {redemption.status === 'pending_merchant' ? 'Cash - Pending' :
                             redemption.status === 'pending_verification' ? 'UPI - Pending' :
                             redemption.status === 'verified' ? 'Verified' :
                             redemption.status}
                          </span>
                          {redemption.paymentMethod && (
                            <span className="text-xs text-gray-500">
                              {redemption.paymentMethod === 'cash' ? '💵' : '📱'} {redemption.paymentMethod.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(redemption.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => setSelectedRedemption(redemption)}
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedRedemption && (
        <RedemptionModal
          redemption={selectedRedemption}
          onClose={() => setSelectedRedemption(null)}
        />
      )}
    </div>
  );
};

// Commission Component
const Commission = ({ restaurant }) => {
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (restaurant) {
      fetchCommissionData();
    }
  }, [restaurant, selectedMonth, selectedYear]);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/transactions/restaurant/${restaurant._id}/commission/${selectedYear}/${selectedMonth}`
      );
      setCommissionData(response.data.commission);
    } catch (error) {
      console.error('Error fetching commission data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    if (tier.includes('0-50K')) return 'bg-gray-100 text-gray-800';
    if (tier.includes('50K-1L')) return 'bg-blue-100 text-blue-800';
    return 'bg-purple-100 text-purple-800';
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Commission Tracking</h1>

      {/* Month/Year Selector */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <option key={i} value={new Date().getFullYear() - i}>
                  {new Date().getFullYear() - i}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Commission Overview */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading...</div>
      ) : commissionData ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Confirmed Volume</h3>
              <p className="text-3xl font-bold text-gray-900">₹{commissionData.totalVolume?.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-2">Only confirmed transactions</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Current Tier</h3>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getTierColor(commissionData.tier)}`}>
                {commissionData.tier}
              </span>
              <p className="text-sm text-gray-500 mt-2">Volume-based</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Commission Rate</h3>
              <p className="text-3xl font-bold text-blue-600">{commissionData.commissionPercentage}%</p>
              <p className="text-sm text-gray-500 mt-2">On original bills</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Commission</h3>
              <p className="text-3xl font-bold text-primary-600">₹{commissionData.totalCommission?.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-2">Payable to LOCU</p>
            </div>
          </div>

          {/* Tier Information */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">₹0 - ₹50,000</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">0%</span>
                </div>
                <p className="text-xs text-gray-500">No commission charged</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">₹50,000 - ₹1,00,000</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">2%</span>
                </div>
                <p className="text-xs text-gray-500">Low commission tier</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Above ₹1,00,000</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">5%</span>
                </div>
                <p className="text-xs text-gray-500">High volume tier</p>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Transaction Breakdown ({commissionData.transactionCount} transactions)
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Total Confirmed Transactions</span>
                <span className="font-medium text-gray-900">{commissionData.transactionCount}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Confirmed Bill Value (Original)</span>
                <span className="font-medium text-gray-900">₹{commissionData.totalVolume?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Your Commission Rate</span>
                <span className="font-medium text-gray-900">{commissionData.commissionPercentage}%</span>
              </div>
              <div className="flex justify-between py-2 bg-primary-50 rounded-lg px-3 mt-3">
                <span className="font-semibold text-gray-900">Commission Payable</span>
                <span className="font-bold text-primary-600 text-lg">₹{commissionData.totalCommission?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
            <h3 className="font-semibold text-blue-900 mb-2">Payment Information</h3>
            <p className="text-sm text-blue-700">
              Commission is calculated on the 1st of each month based on your total transaction volume.
              You will receive an invoice and payment is due within 7 days. Commission is calculated on
              the original bill amounts before discounts.
            </p>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No commission data available for this period
        </div>
      )}
    </div>
  );
};

const Photos = ({ restaurant }) => {
  const [activeTab, setActiveTab] = useState('ambience');
  const [allPhotos, setAllPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileInputRef = useRef(null);

  // Filter photos by active tab
  const photos = allPhotos.filter(photo => photo.category === activeTab);

  useEffect(() => {
    if (restaurant?._id) {
      fetchPhotos();
    }
  }, [restaurant?._id]);

  const fetchPhotos = async () => {
    if (!restaurant?._id) return;

    try {
      const response = await axios.get(`${API_URL}/restaurants/${restaurant._id}`);
      setAllPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUpload(files);
    }
  };

  // Compress image using Canvas API to stay within Vercel's 4.5MB body limit
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      // Skip non-image files
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          let { width, height } = img;

          // Scale down if wider than maxWidth
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressed = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressed);
            },
            'image/jpeg',
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (files) => {
    if (!restaurant?._id) {
      alert('Please select a restaurant first');
      return;
    }

    setUploading(true);

    try {
      // Compress all images before uploading
      const compressedFiles = await Promise.all(
        Array.from(files).map((file) => compressImage(file))
      );

      // Upload in batches of 3 to stay within Vercel's 4.5MB body limit
      const BATCH_SIZE = 3;
      let latestPhotos = null;

      for (let i = 0; i < compressedFiles.length; i += BATCH_SIZE) {
        const batch = compressedFiles.slice(i, i + BATCH_SIZE);
        const formData = new FormData();
        batch.forEach((file) => {
          formData.append('photos', file);
        });
        formData.append('category', activeTab);

        const response = await axios.post(`${API_URL}/restaurants/${restaurant._id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.photos) {
          latestPhotos = response.data.photos;
        }
      }

      if (latestPhotos) {
        setAllPhotos(latestPhotos);
      }
      alert(`${compressedFiles.length} photos uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert(error.response?.data?.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/restaurants/${restaurant._id}/photos/${photoId}`);
      // Remove photo from local state
      setAllPhotos(allPhotos.filter(photo => photo._id !== photoId));
      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert(error.response?.data?.message || 'Failed to delete photo. Please try again.');
    }
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the filtered photos for this category
    const reordered = [...photos];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    // Update local state immediately for responsiveness
    const otherPhotos = allPhotos.filter(p => p.category !== activeTab);
    setAllPhotos([...otherPhotos, ...reordered]);
    setDragIndex(null);
    setDragOverIndex(null);

    // Persist the full order (all categories) to the server
    const newAll = [...otherPhotos, ...reordered];
    try {
      await axios.patch(`${API_URL}/restaurants/${restaurant._id}/photos/reorder`, {
        photoIds: newAll.map(p => p._id)
      });
    } catch (error) {
      console.error('Error saving photo order:', error);
      fetchPhotos(); // revert on failure
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Photos</h1>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-2 border-b border-gray-200">
            {['Ambience', 'Food'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === tab.toLowerCase()
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{activeTab}</h2>
            <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
              <option>All photos</option>
            </select>
          </div>

          {photos.length === 0 && !uploading ? (
            <div className="text-center py-12">
              <div className="w-32 h-32 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <PhotoIcon className="h-16 w-16 text-gray-400" />
              </div>
              <p className="text-lg font-semibold text-gray-900 mb-2">
                Your restaurant does not have any {activeTab} photos
              </p>
              <p className="text-gray-600 mb-6">
                Adding photos of your restaurant will help users understand the vibe of your
                restaurant better
              </p>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="photo-upload"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-5 w-5" />
                      Add photos
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div
                  key={photo._id}
                  className={`relative group cursor-grab active:cursor-grabbing ${
                    dragOverIndex === index ? 'ring-2 ring-blue-500 ring-offset-2 rounded-lg' : ''
                  } ${dragIndex === index ? 'opacity-40' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                >
                  <img
                    src={photo.url.startsWith('http') ? photo.url : `${API_URL.replace('/api', '')}${photo.url}`}
                    alt={photo.caption}
                    className="w-full h-48 object-cover rounded-lg pointer-events-none"
                  />
                  <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-0.5 rounded-full">
                    {index + 1}
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleDeletePhoto(photo._id)}
                      className="opacity-0 group-hover:opacity-100 text-white hover:text-red-400"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
              {/* Add more photos button when photos exist */}
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg h-48 flex items-center justify-center hover:border-blue-500 transition-colors cursor-pointer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="photo-upload-existing"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex flex-col items-center gap-2 text-gray-500 hover:text-blue-600"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  ) : (
                    <>
                      <PlusIcon className="h-8 w-8" />
                      <span className="text-sm font-medium">Add more</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ restaurant }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: restaurant?.name || '',
    cuisine: restaurant?.cuisine || '',
    address: restaurant?.address || '',
    phone: restaurant?.phone || '',
    description: restaurant?.description || '',
    website: restaurant?.website || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || '',
        cuisine: restaurant.cuisine || '',
        address: restaurant.address || '',
        phone: restaurant.phone || '',
        description: restaurant.description || '',
        website: restaurant.website || ''
      });
    }
  }, [restaurant]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/restaurants/${restaurant._id}`, formData);
      alert('Restaurant information updated successfully!');
      setEditing(false);
      window.location.reload();
    } catch (error) {
      console.error('Error updating restaurant:', error);
      alert(error.response?.data?.message || 'Failed to update restaurant information.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Restaurant Info</h1>

      <div className="bg-white rounded-lg shadow p-6">
        {!editing ? (
          <>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Restaurant Name
                </label>
                <p className="text-base text-gray-900">{restaurant?.name || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cuisine Type
                </label>
                <p className="text-base text-gray-900">{restaurant?.cuisine || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <p className="text-base text-gray-900">{restaurant?.address || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <p className="text-base text-gray-900">{restaurant?.phone || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <p className="text-base text-gray-900">{restaurant?.description || 'No description provided'}</p>
              </div>

              {restaurant?.website && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <a
                    href={restaurant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-blue-600 hover:text-blue-700 underline"
                  >
                    {restaurant.website}
                  </a>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Edit Information
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Restaurant Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cuisine Type
                </label>
                <input
                  type="text"
                  name="cuisine"
                  value={formData.cuisine}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tell customers about your restaurant..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    name: restaurant?.name || '',
                    cuisine: restaurant?.cuisine || '',
                    address: restaurant?.address || '',
                    phone: restaurant?.phone || '',
                    description: restaurant?.description || '',
                    website: restaurant?.website || ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Profile Component - Merchants only need email
const Profile = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/users/profile`, formData);
      alert('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getUserInitial = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'M';
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Profile</h1>

      <div className="bg-white rounded-lg shadow p-8">
        {!editing ? (
          <div>
            <div className="flex flex-col items-center mb-8">
              <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4">
                {getUserInitial()}
              </div>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <p className="text-base text-gray-900">{user?.email || 'N/A'}</p>
              </div>
            </div>

            <div className="mt-8">
              <button
                onClick={() => setEditing(true)}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Edit Email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col items-center mb-8">
              <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-3xl font-bold mb-4">
                <PencilIcon className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Edit Email</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Used for invoices, settlements, and important updates</p>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Submit'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    email: user?.email || ''
                  });
                }}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LocuMerchantDashboard;
